import { z } from "zod";
/**
 * Presentation-only customization for a scenario chart. It never changes the
 * computed values, only how they are coloured. The same shape is stored as a
 * scenario-level default and, optionally, as a per-dashboard block override
 * (see `resolveScenarioDisplayConfig`).
 */
export const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const hexColorSchema = z.string().regex(HEX_COLOR_REGEX);
const colorMapSchema = z.record(hexColorSchema);
export const scenarioDisplayConfigSchema = z
    .object({
    sectorColorMap: colorMapSchema.optional(),
    energySourceColorMap: colorMapSchema.optional(),
})
    .strict();
export const DEFAULT_SCENARIO_DISPLAY_CONFIG = {};
/**
 * Parses an untrusted JSON value (from the DB or GraphQL) into a display
 * config, returning `null` when the value is absent or malformed so callers
 * fall back to the default rather than throwing.
 */
export function parseScenarioDisplayConfig(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const result = scenarioDisplayConfigSchema.safeParse(value);
    return result.success ? result.data : null;
}
function mergeColorMap(base, override) {
    if (!base && !override) {
        return undefined;
    }
    return { ...(base ?? {}), ...(override ?? {}) };
}
/**
 * Resolves the effective config from the scenario-level default and an optional
 * dashboard-block override. Color maps merge shallowly so a block can recolour
 * a single sector or energy source without dropping the rest.
 */
export function resolveScenarioDisplayConfig(scenarioDefault, blockOverride) {
    const base = scenarioDefault ?? DEFAULT_SCENARIO_DISPLAY_CONFIG;
    return {
        sectorColorMap: mergeColorMap(base.sectorColorMap, blockOverride?.sectorColorMap),
        energySourceColorMap: mergeColorMap(base.energySourceColorMap, blockOverride?.energySourceColorMap),
    };
}
